import React, { useState } from "react";
import { useTurnstile } from "@/components/Turnstile";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { authClient, setPlatformToken } from "@/lib/musicApi";
import { trackLogin } from "@/lib/track";
import { usePlatformAuth } from "@/contexts/PlatformAuthContext";
import { PasswordInput } from "@/components/PasswordInput";

const field = { background: "var(--bg-subtle)", border: "1px solid var(--border-color)", color: "var(--text-primary)" };

// Shopper / licensee sign-in. Deliberately distinct from the subscriber Login.jsx:
// no "Join for $X" upsell, no Google/2FA subscriber marketing. Offers password
// login plus a passwordless magic link, so shadow accounts (guest shop/licensing
// buyers who never set a password) can still get back in.
export const ShopperLogin = ({ next, onSuccess, embedded = false,
  heading = "Log in to your purchases",
  subheading = "Access your orders, downloads and licenses." }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = usePlatformAuth();
  const dest = next || new URLSearchParams(location.search).get("next") || "/account/purchases";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [linkSent, setLinkSent] = useState(false);

  const finish = async (data) => {
    if (data.token) setPlatformToken(data.token);
    if (data.token) trackLogin("password");
    await refresh();
    if (onSuccess) onSuccess(); else navigate(dest, { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try {
      const { data } = await authClient.post("/subscriber/login", { email: email.toLowerCase(), password });
      if (data.requires_2fa) { setError("This account uses two-factor login. Please use the main log in page."); setBusy(false); return; }
      await finish(data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : (d?.message || "Could not log in. Check your email and password."));
      setBusy(false);
    }
  };

  const tsMagic = useTurnstile("magic_link");
  const sendLink = async () => {
    if (!email) { setError("Enter your email first, then request a link."); return; }
    setError(""); setBusy(true);
    try { await authClient.post("/magic-link", { email: email.toLowerCase(), turnstile_token: await tsMagic() }); } catch { /* generic, no enumeration */ }
    setLinkSent(true); setBusy(false);
  };

  const Card = (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
      data-testid="shopper-login" className="w-full max-w-sm rounded-2xl p-7" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-color)" }}>
      <h1 className="text-2xl font-bold mb-1" style={{ color: "var(--text-primary)" }}>{heading}</h1>
      <p className="text-sm mb-5" style={{ color: "var(--text-secondary)" }}>{subheading}</p>
      {linkSent ? (
        <div data-testid="shopper-login-link-sent">
          <p className="text-sm mb-4" style={{ color: "var(--text-secondary)" }}>If that email has purchases with us, a one-tap sign-in link is on its way. Check your inbox and spam folder.</p>
          <p className="text-xs mb-4 rounded-lg p-3" data-testid="shopper-login-no-account-hint" style={{ color: "var(--text-secondary)", background: "var(--bg-subtle)", border: "1px solid var(--border-color)" }}>New here? You get an account automatically when you check out. Add the items you want, complete checkout, and you can set a password from there.</p>
          <button onClick={() => { setLinkSent(false); setError(""); }} data-testid="shopper-login-link-back" className="text-sm font-medium flex items-center gap-1" style={{ color: "var(--accent-primary)" }}><ArrowLeft size={13} /> Back to log in</button>
        </div>
      ) : (
        <>
          {error && <p data-testid="shopper-login-error" className="text-sm mb-3" style={{ color: "#ef4444" }}>{error}</p>}
          <form onSubmit={submit}>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" data-testid="shopper-login-email" autoComplete="email"
              className="w-full px-4 py-3 rounded-lg text-sm mb-3 outline-none" style={field} />
            <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" data-testid="shopper-login-password" autoComplete="current-password"
              wrapperClassName="relative mb-4" className="w-full px-4 py-3 rounded-lg text-sm outline-none" style={field} />
            <button type="submit" disabled={busy} data-testid="shopper-login-submit" className="w-full py-3 rounded-full text-sm font-semibold" style={{ background: "var(--accent-primary)", color: "#fff", opacity: busy ? 0.6 : 1 }}>{busy ? "..." : "Log in"}</button>
          </form>
          <div className="flex items-center gap-3 my-4">
            <span className="flex-1 h-px" style={{ background: "var(--border-color)" }} /><span className="text-xs" style={{ color: "var(--text-secondary)" }}>or</span><span className="flex-1 h-px" style={{ background: "var(--border-color)" }} />
          </div>
          <button type="button" onClick={sendLink} disabled={busy} data-testid="shopper-login-magic" className="w-full py-3 rounded-full text-sm font-medium" style={{ border: "1px solid var(--border-color)", color: "var(--text-primary)" }}>Email me a sign-in link</button>
          <p className="text-xs text-center mt-5" style={{ color: "var(--text-secondary)" }}>No password yet? Use the sign-in link and set one from your account.</p>
        </>
      )}
    </motion.div>
  );

  if (embedded) return Card;
  return (
    <main data-testid="shopper-login-page" className="min-h-screen flex flex-col items-center justify-center px-4 pt-20 pb-16" style={{ background: "var(--bg-primary)" }}>
      <div className="w-full max-w-sm mb-4">
        <Link to="/shop" data-testid="shopper-login-back" className="inline-flex items-center gap-1.5 text-sm font-medium" style={{ color: "var(--text-secondary)" }}>
          <ArrowLeft size={15} /> Back to Shop
        </Link>
      </div>
      {Card}
    </main>
  );
};

export default ShopperLogin;
